import { useEffect, useMemo, useState } from 'react';
import { isEqual, keyBy } from 'lodash';
import { message } from 'components';
import { usePrevious } from 'hooks';
import errorReporter from '@app/errorReporter';
import { updateTour } from 'api/TourController';
import { TourAutoSaveStatus } from '../types';

type AutoSaveState = {
  name: string;
  date: Date | number;
  tourItemOrder: string[];
  tourItemTimes: {
    tourItemId: string;
    time?: Date;
    timeEnd?: Date;
  }[];
  tourItemNotes: {
    tourItemId: string;
    note: string;
  }[];
  buyers: string[];
  pendingBuyers?: boolean;
};

const DEBOUNCE_TIMEOUT = 3000;

const MAX_ERROR_LIMIT = 2;

// Only used when prev and cur exists
const hasOrderChanged = (prev: string[], cur: string[]) =>
  prev.length === cur.length && !isEqual(prev, cur);

const timeChangedItems = (
  prev: {
    tourItemId: string;
    time?: Date;
    timeEnd?: Date;
  }[],
  cur: {
    tourItemId: string;
    time?: Date;
    timeEnd?: Date;
  }[],
) => {
  const prevTimeMap = keyBy(prev, 'tourItemId');

  return cur.filter(
    item =>
      prevTimeMap[item.tourItemId]?.time?.getTime() !== item.time?.getTime() ||
      prevTimeMap[item.tourItemId]?.timeEnd?.getTime() !==
        item.timeEnd?.getTime(),
  ) as {
    tourItemId: string;
    time: Date;
    timeEnd: Date;
  }[];
};

const noteChangedItems = (
  prev: {
    tourItemId: string;
    note?: string;
  }[],
  cur: {
    tourItemId: string;
    note?: string;
  }[],
) => {
  const prevTimeMap = keyBy(prev, 'tourItemId');

  return cur.filter(
    item => prevTimeMap[item.tourItemId]?.note !== item.note,
  ) as {
    tourItemId: string;
    note?: string;
  }[];
};

const hasStateChanged = (prev: AutoSaveState, cur: AutoSaveState) => 
  // Has to compare attributes separately since tourItemOrder auto-update
  // must be only triggered on order change, not add/remove item.
   Boolean(
    prev &&
      cur &&
      (prev.name !== cur.name ||
        // Here, date fields are 'number' types
        prev.date !== cur.date ||
        !isEqual(prev.buyers, cur.buyers) ||
        hasOrderChanged(prev.tourItemOrder, cur.tourItemOrder) ||
        timeChangedItems(prev.tourItemTimes, cur.tourItemTimes).length > 0 ||
        noteChangedItems(prev.tourItemNotes, cur.tourItemNotes).length > 0),
  )
;

const useTourAutoSave = (
  tourId: string,
  currentState: AutoSaveState | null,
  setPendingBuyers: (pendingBuyers: boolean) => void | null,
) => {
  const [autoSaveStatus, setAutoSaveStatus] = useState(
    TourAutoSaveStatus.SAVED,
  );
  const [lastSavedState, setLastSavedState] = useState<AutoSaveState | null>(
    null,
  );
  const [errorCount, setErrorCount] = useState(0);

  const parsedState = useMemo(() => currentState
      ? {
          ...currentState,
          date: (currentState.date as Date).getTime(),
        }
      : null, [currentState]);

  const prevState = usePrevious<AutoSaveState | null>(parsedState);

  useEffect(() => {
    if (hasStateChanged(prevState, parsedState)) {
      setErrorCount(0);
    }
  }, [parsedState, prevState]);

  useEffect(() => {
    let debounce: NodeJS.Timeout;
    const stateChanged = hasStateChanged(lastSavedState, parsedState);
    // This is to be passed on to BE for performance optimization, to not fetch
    // the order mapping in the BE when there is no tour item order change.
    const orderChanged =
      stateChanged &&
      hasOrderChanged(lastSavedState.tourItemOrder, parsedState.tourItemOrder);
    const tourItemTimes = stateChanged
      ? timeChangedItems(
          lastSavedState.tourItemTimes,
          parsedState.tourItemTimes,
        )
      : [];
    const tourItemNotes = stateChanged
      ? noteChangedItems(
          lastSavedState.tourItemNotes,
          parsedState.tourItemNotes,
        )
      : [];
    if (
      // Update when state differs from last saved state
      stateChanged &&
      // AND if the number of tries didn't exceed the maximum
      errorCount < MAX_ERROR_LIMIT
    ) {
      if (hasStateChanged(prevState, parsedState) && !errorCount) {
        // Unsaved status at no error count,
        // first try on the new state update
        setAutoSaveStatus(TourAutoSaveStatus.UNSAVED);
      }

      debounce = setTimeout(async () => {
        try {
          setAutoSaveStatus(TourAutoSaveStatus.SAVING);
          const { pendingBuyers } = await updateTour(tourId, {
            ...parsedState,
            tourItemOrder: orderChanged ? parsedState.tourItemOrder : [],
            tourItemTimes,
            tourItemNotes,
          });
          if (setPendingBuyers) setPendingBuyers(pendingBuyers);
          setErrorCount(0);
          setLastSavedState(parsedState);

          // should not transit from other state to SAVED directly
          // if in other state, that means new changes have been made
          setAutoSaveStatus(prevAutoSaveStatus =>
            prevAutoSaveStatus === TourAutoSaveStatus.SAVING
              ? TourAutoSaveStatus.SAVED
              : prevAutoSaveStatus,
          );
        } catch (err) {
          errorReporter.error(err);
          const errorMsg = !errorCount
            ? 'Error auto-saving tour changes'
            : 'Error retrying tour auto-save';
          const errorState = !errorCount
            ? TourAutoSaveStatus.ERROR
            : TourAutoSaveStatus.ERROR_RETRIED;
          message.error(errorMsg);
          setErrorCount(prev => prev + 1);
          setAutoSaveStatus(errorState);
        }
      }, DEBOUNCE_TIMEOUT * (errorCount + 1));
    } else if (errorCount < MAX_ERROR_LIMIT) {
      if (
        !parsedState ||
        !lastSavedState ||
        // item add && remove is currently handled outside of auto-save,
        // therefore need to manually update state when that happens
        prevState.tourItemOrder.length !== parsedState.tourItemOrder.length
      ) {
        setLastSavedState(parsedState);
      }
      setAutoSaveStatus(TourAutoSaveStatus.SAVED);
    }

    return () => {
      if (debounce) {
        clearTimeout(debounce);
      }
    };
  }, [tourId, errorCount, parsedState, lastSavedState, prevState]);

  return autoSaveStatus;
};

export default useTourAutoSave;
